import { useEffect, useState, useReducer } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTimeoutFn } from 'react-use';
import { FaPlus, FaMinus } from 'react-icons/fa';

import Button from './inputs/Button';
import { FaArrowRight } from 'react-icons/fa';
import {
  SUBSCRIPTION_DETAILS,
  USER_DETAILS_QUERY,
  SUBSCRIPTION_STATUS,
  CUSTOMER_QUERY,
} from '../queries';
import { capitalize, currency } from '../utils';
import { getSubscriptionPrice } from '../helpers/pricing';
import ActionModal from './ActionModal';
import ToastNotification from './ToastNotification';
import {
  CLEAR_NEXT_ACTION,
  CLEAR_SUCCESS,
  initialState,
  reducer,
  START_ACTION,
} from '../reducers/actionReducer';
import DowngradeToFreePlan from './actions/DowngradeToFreePlan';
import ReactivateCancelledPlan from './actions/ReactivateCancelledPlan';
import DowngradeToLowerPlan from './actions/DowngradeToLowerPlan';
import LoadingOverlay from './LoadingOverlay';
import { useAuth } from '../hooks/useAuth';
import Toggle from './Toggle';
import SubscriptionInvoicesTable from './SubscriptionInvoicesTable';
import RequestCustomPlan from './actions/RequestCustomPlan';

export const plans = [
  {
    name: 'free',
    displayName: 'Free',
    description:
      'Never pay to access the platform. But upgrading does have its perks.',
  },
  {
    name: 'bronze',
    displayName: 'Bronze',
    description: 'Perfect for users who want unlimited profile reports.',
  },
  {
    name: 'silver',
    displayName: 'Silver',
    description: 'Treat yourself to some savings with 15% off your orders.',
  },
  {
    name: 'gold',
    displayName: 'Gold',
    description:
      'Build a custom plan with the features you need. Contact us for a quote.',
  },
];

function Plan({
  name,
  displayName,
  description,
  currentPlan,
  upgrade,
  dispatchAction,
  currentPeriodEnd,
  cancelAtPeriodEnd,
  hasPaidSubscription,
  recurringInterval,
  downgradeScheduledTo,
  displayRecurringInterval,
}) {
  const navigate = useNavigate();

  function handleUpgradeDowngradeClick() {
    if (name === 'gold') {
      dispatchAction({
        type: START_ACTION,
        payload: {
          name: 'Contact Us for a Custom Plan',
          component: <RequestCustomPlan dispatchAction={dispatchAction} />,
        },
      });
      return;
    }

    if (upgrade) {
      navigate('subscription/order', {
        state: {
          orderDetails: {
            plan: name,
            recurringInterval: displayRecurringInterval,
            upgrading: hasPaidSubscription,
          },
        },
      });
      return;
    }

    if (name === 'free') {
      dispatchAction({
        type: START_ACTION,
        payload: {
          name: 'Downgrade to Free Plan',
          component: (
            <DowngradeToFreePlan
              dispatchAction={dispatchAction}
              currentPeriodEnd={currentPeriodEnd}
            />
          ),
        },
      });
    }

    if (currentPlan && cancelAtPeriodEnd) {
      dispatchAction({
        type: START_ACTION,
        payload: {
          name: `Reactivate ${displayName} Plan`,
          component: (
            <ReactivateCancelledPlan
              dispatchAction={dispatchAction}
              currentPeriodEnd={currentPeriodEnd}
            />
          ),
        },
      });
    }

    if (!upgrade && name !== 'free' && !currentPlan && !cancelAtPeriodEnd) {
      dispatchAction({
        type: START_ACTION,
        payload: {
          name: `Downgrade to ${capitalize(name)} Plan`,
          component: (
            <DowngradeToLowerPlan
              dispatchAction={dispatchAction}
              targetPlan={name}
              recurringInterval={recurringInterval}
              currentPeriodEnd={currentPeriodEnd}
            />
          ),
        },
      });
    }
  }

  let buttonLabel = 'Downgrade';

  if (upgrade) buttonLabel = 'Upgrade';
  if (name === 'gold' && !currentPlan) buttonLabel = 'Contact Us';
  if (currentPlan) buttonLabel = 'Current Plan';
  if (name === 'free' && cancelAtPeriodEnd) buttonLabel = 'Set to Downgrade';
  if (currentPlan && cancelAtPeriodEnd)
    buttonLabel = `Reactivate ${displayName} Plan`;

  const price = getSubscriptionPrice(name, displayRecurringInterval);

  return (
    <div className="flex flex-1 flex-col h-full justify-center xl:h-72">
      <div
        className={`flex flex-col flex-grow h-full justify-between rounded-lg p-4 text-center ${
          currentPlan ? 'bg-secondary text-white' : 'bg-gray-200'
        }`}
      >
        <p className="font-bold mb-2 text-xl">{displayName}</p>
        <div className="flex flex-col mb-4">
          <p className="font-light text-3xl ">
            {name === 'gold' ? 'Custom' : currency(price)}
          </p>
          {price > 0 && (
            <span className="ml-1 text-sm">
              / {displayRecurringInterval === 'monthly' ? 'month' : 'quarter'}
            </span>
          )}
        </div>
        <p className={`${currentPlan ? 'text-white' : 'text-gray-600 '}`}>
          {description}
        </p>
      </div>
      <div className="flex flex-grow-0">
        <Button
          className={`w-full mt-3 ${
            currentPlan
              ? `border-secondary text-secondary ${
                  cancelAtPeriodEnd
                    ? 'hover:bg-secondary hover:text-white'
                    : 'cursor-default'
                }`
              : ''
          }`}
          primary={upgrade || undefined}
          type="button"
          onClick={handleUpgradeDowngradeClick}
          disabled={
            (!currentPlan && cancelAtPeriodEnd) ||
            (name !== 'free' && downgradeScheduledTo)
          }
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}

export default function Plans({ currentPlan = 'free' }) {
  const { getUser } = useAuth();

  const { data } = useQuery(USER_DETAILS_QUERY);
  const userDetails = data?.userDetails;
  const userPlan = userDetails?.plan;

  const { data: dataSubscriptionDetails } = useQuery(SUBSCRIPTION_DETAILS);
  const subscriptionDetails = dataSubscriptionDetails?.subscriptionDetails;

  const { data: dataCustomer } = useQuery(CUSTOMER_QUERY);
  const customer = dataCustomer?.customer;
  const balance = customer?.balance;

  const [currentPlanIdx, setCurrentPlanIdx] = useState(0);
  const [displayRecurringInterval, setDisplayRecurringInterval] = useState(0);

  const recurringIntervals = ['Billed Monthly', 'Billed Quarterly'];

  const [showInvoices, setShowInvoices] = useState(false);

  useEffect(() => {
    if (subscriptionDetails?.recurringInterval != null) {
      setDisplayRecurringInterval(
        subscriptionDetails?.recurringInterval === 'monthly' ? 0 : 1
      );
    }
  }, [subscriptionDetails?.recurringInterval]);

  useEffect(() => {
    if (currentPlan || userPlan) {
      setCurrentPlanIdx(
        plans.findIndex((plan) => plan.name === (userPlan || currentPlan))
      );
    }
  }, [currentPlan, userPlan]);

  const [actionState, dispatchAction] = useReducer(reducer, initialState);
  const { success, nextAction } = actionState;

  const [
    checkSubscriptionStatus,
    {
      data: dataCheckSubscriptionStatus,
      called: calledCheckSubscriptionStatus,
      startPolling,
      stopPolling,
    },
  ] = useLazyQuery(SUBSCRIPTION_STATUS, {
    fetchPolicy: 'network-only',
  });
  const subscriptionStatus = dataCheckSubscriptionStatus?.subscriptionStatus;
  const cancelAtPeriodEnd = subscriptionStatus?.cancelAtPeriodEnd;

  const [subscriptionPollingTimeout, setSubscriptionPollingTimeout] =
    useState(null);
  const [pollingSubscriptionStatus, setPollingSubscriptionStatus] =
    useState(false);

  useTimeoutFn(() => {
    if (subscriptionPollingTimeout != null) {
      stopPolling();
    }
  }, subscriptionPollingTimeout);

  useEffect(() => {
    if (
      (success && nextAction === 'poll-subscription-status-for-cancelled') ||
      nextAction === 'poll-subscription-status-for-not-cancelled'
    ) {
      setTimeout(() => dispatchAction({ type: CLEAR_SUCCESS }), 5000);
      checkSubscriptionStatus();
      startPolling(300);
      setSubscriptionPollingTimeout(15000);
      setPollingSubscriptionStatus(true);
    }
  }, [success, nextAction, checkSubscriptionStatus, startPolling]);

  useEffect(() => {
    async function runEffect() {
      await getUser({ force: true });
      dispatchAction({
        type: CLEAR_NEXT_ACTION,
      });
      setPollingSubscriptionStatus(false);
      stopPolling();
    }

    if (
      success &&
      pollingSubscriptionStatus &&
      calledCheckSubscriptionStatus &&
      ((cancelAtPeriodEnd &&
        nextAction === 'poll-subscription-status-for-cancelled') ||
        (!cancelAtPeriodEnd &&
          nextAction === 'poll-subscription-status-for-not-cancelled'))
    ) {
      runEffect();
    }
  }, [
    calledCheckSubscriptionStatus,
    cancelAtPeriodEnd,
    getUser,
    nextAction,
    pollingSubscriptionStatus,
    stopPolling,
    subscriptionStatus,
    success,
  ]);

  return (
    <>
      <div className="flex flex-col pt-4">
        <div className="flex flex-row justify-between items-center mb-6">
          <h4 className="mb-0">Modify Plan</h4>
          <Toggle
            options={recurringIntervals}
            value={displayRecurringInterval}
            onChange={(idx) => setDisplayRecurringInterval(idx)}
          />
          <div className="flex items-center">
            <a
              className="flex items-center font-bold hover:text-primary cursor-pointer"
              href="#"
              target="_blank"
              rel="noreferrer"
            >
              Compare Plans
              <FaArrowRight className="ml-2" />
            </a>
          </div>
        </div>
        <div className="flex space-x-4">
          {plans.map((plan, idx) => (
            <Plan
              {...plan}
              currentPlan={idx === currentPlanIdx}
              upgrade={idx > currentPlanIdx}
              key={idx}
              dispatchAction={dispatchAction}
              currentPeriodEnd={subscriptionDetails?.currentPeriodEnd}
              cancelAtPeriodEnd={subscriptionDetails?.cancelAtPeriodEnd}
              hasPaidSubscription={!!subscriptionDetails?.id}
              recurringInterval={subscriptionDetails?.recurringInterval}
              downgradeScheduledTo={subscriptionDetails?.downgradeScheduledTo}
              displayRecurringInterval={
                displayRecurringInterval === 0 ? 'monthly' : 'quarterly'
              }
            />
          ))}
        </div>
        {subscriptionDetails?.id && (
          <div className="flex flex-col pt-10">
            <h4 className="mb-4">Current Subscription</h4>
            <div className="flex flex-col bg-gray-200 rounded-lg p-5 space-y-2">
              <div className="flex flex-row justify-between">
                <div>
                  <span className="font-bold">Next Invoice:</span>{' '}
                  {format(
                    new Date(Number(subscriptionDetails.currentPeriodEnd)),
                    'PPP'
                  )}
                </div>
                <div className="flex flex-row space-x-1">
                  {subscriptionDetails.status === 'active' && (
                    <div className="bg-secondary text-white shadow-lg rounded py-0.5 px-1.5 font-semibold text-sm">
                      Active
                    </div>
                  )}
                  {subscriptionDetails.cancelAtPeriodEnd && (
                    <div className="bg-red-500 text-white shadow-lg rounded py-0.5 px-1.5 font-semibold text-sm">
                      Expires at end of period
                    </div>
                  )}
                  {subscriptionDetails.downgradeScheduledTo && (
                    <div className="bg-red-500 text-white shadow-lg rounded py-0.5 px-1.5 font-semibold text-sm">
                      Downgrades to{' '}
                      {capitalize(subscriptionDetails.downgradeScheduledTo)} at
                      end of period
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-row">
                <span className="font-bold mr-1">Recurring:</span>{' '}
                {capitalize(subscriptionDetails.recurringInterval)}
              </div>
              <div
                className="flex items-center justify-between cursor-pointer hover:bg-gray-100 rounded-lg px-3 py-1.5"
                onClick={() => setShowInvoices((prevState) => !prevState)}
              >
                <div className="flex items-center font-bold">
                  {' '}
                  {showInvoices ? (
                    <FaMinus className="mr-2" />
                  ) : (
                    <FaPlus className="mr-2" />
                  )}
                  Show Invoices
                </div>
                {balance && <div>Credit Balance: {currency(balance)}</div>}
              </div>
              {showInvoices && (
                <div className="flex pt-4">
                  <SubscriptionInvoicesTable />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {actionState.inProgress && (
        <ActionModal
          actionState={actionState}
          dispatchAction={dispatchAction}
          show={actionState.inProgress}
        />
      )}
      <ToastNotification
        message={actionState.success}
        success={true}
        show={actionState.success}
      />
      {pollingSubscriptionStatus && (
        <LoadingOverlay message="Updating your subscription status..." />
      )}
    </>
  );
}
